/**
 * Native (editable) PowerPoint chart specs for hybrid dashboard PPTX export.
 * Complex / unsupported charts stay as PNG screenshots.
 */

export type PptxNativeChartKind = 'bar' | 'barH' | 'line' | 'pie';

export type PptxNativeChartSeries = {
  name: string;
  values: number[];
};

export type PptxNativeChartSpec = {
  kind: PptxNativeChartKind;
  labels: string[];
  series: PptxNativeChartSeries[];
};

export const PPTX_NATIVE_CHART_ATTR = 'data-pptx-native-chart';

const NATIVE_KINDS = new Set<PptxNativeChartKind>(['bar', 'barH', 'line', 'pie']);

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** True when labels + series are safe for pptxgenjs addChart. */
export function isValidPptxNativeChart(value: unknown): value is PptxNativeChartSpec {
  if (!value || typeof value !== 'object') return false;
  const spec = value as Partial<PptxNativeChartSpec>;
  if (!NATIVE_KINDS.has(spec.kind as PptxNativeChartKind)) return false;
  if (!Array.isArray(spec.labels) || spec.labels.length === 0) return false;
  if (!Array.isArray(spec.series) || spec.series.length === 0) return false;
  const labelCount = spec.labels.length;
  if (spec.labels.some((label) => typeof label !== 'string' || !label.trim())) return false;
  for (const series of spec.series) {
    if (!series || typeof series.name !== 'string' || !series.name.trim()) return false;
    if (!Array.isArray(series.values) || series.values.length !== labelCount) return false;
    if (!series.values.every(isFiniteNumber)) return false;
  }
  // Pie: single series only (PowerPoint pie semantics).
  if (spec.kind === 'pie' && spec.series.length !== 1) return false;
  return true;
}

export function serializePptxNativeChart(spec: PptxNativeChartSpec): string {
  if (!isValidPptxNativeChart(spec)) {
    throw new Error('Invalid PptxNativeChartSpec');
  }
  return JSON.stringify(spec);
}

export function parsePptxNativeChartJson(raw: string | null | undefined): PptxNativeChartSpec | null {
  if (!raw?.trim()) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isValidPptxNativeChart(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Read opt-in native chart JSON from a capture section (or a descendant). */
export function readPptxNativeChartFromElement(section: HTMLElement): PptxNativeChartSpec | null {
  const self = section.getAttribute(PPTX_NATIVE_CHART_ATTR);
  const fromSelf = parsePptxNativeChartJson(self);
  if (fromSelf) return fromSelf;
  const nested = section.querySelector<HTMLElement>(`[${PPTX_NATIVE_CHART_ATTR}]`);
  if (!nested || nested === section) return null;
  return parsePptxNativeChartJson(nested.getAttribute(PPTX_NATIVE_CHART_ATTR));
}

/**
 * Recharts sections that should stay screenshots even if someone attaches partial data.
 * Radar / composed multi-geometry / scatter are not mapped to simple pptxgenjs charts.
 */
export function sectionLooksTooComplexForNativeChart(section: HTMLElement): boolean {
  if (section.querySelector('.recharts-radar, .recharts-scatter')) return true;
  const hasBar = section.querySelector('.recharts-bar') != null;
  const hasLine = section.querySelector('.recharts-line') != null;
  const hasArea = section.querySelector('.recharts-area') != null;
  const hasPie = section.querySelector('.recharts-pie') != null;
  const geometryCount = [hasBar, hasLine, hasArea, hasPie].filter(Boolean).length;
  return geometryCount > 1;
}

/** Prefer explicit JSON; refuse complex Recharts mixes unless data is valid (caller still decides). */
export function resolveNativeChartForSection(section: HTMLElement): PptxNativeChartSpec | null {
  const spec = readPptxNativeChartFromElement(section);
  if (!spec) return null;
  // Allow explicit override even on composed charts (author opted in with clean series).
  return spec;
}

export function buildSingleSeriesNativeChart(
  kind: PptxNativeChartKind,
  labels: string[],
  values: number[],
  seriesName: string,
): PptxNativeChartSpec | null {
  const spec: PptxNativeChartSpec = {
    kind,
    labels: labels.map((label) => label.trim()).filter(Boolean),
    series: [{ name: seriesName.trim() || 'ค่า', values }],
  };
  if (spec.labels.length !== values.length) return null;
  return isValidPptxNativeChart(spec) ? spec : null;
}

/**
 * Horizontal department bars become unreadable when dozens of categories share one slide.
 * Keep one chart per slide; split dense barH series so every label stays visible.
 */
export const PPTX_BARH_CATEGORIES_PER_SLIDE = 12;

export function chunkPptxNativeChart(
  spec: PptxNativeChartSpec,
  maxPerSlide: number = PPTX_BARH_CATEGORIES_PER_SLIDE,
): PptxNativeChartSpec[] {
  if (!isValidPptxNativeChart(spec)) return [];
  if (spec.kind !== 'barH') return [spec];
  const limit = Math.max(1, Math.floor(maxPerSlide));
  if (spec.labels.length <= limit) return [spec];

  const chunks: PptxNativeChartSpec[] = [];
  for (let start = 0; start < spec.labels.length; start += limit) {
    const end = start + limit;
    chunks.push({
      kind: spec.kind,
      labels: spec.labels.slice(start, end),
      series: spec.series.map((series) => ({
        name: series.name,
        values: series.values.slice(start, end),
      })),
    });
  }
  return chunks;
}
